import { useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Flex,
  Input,
  NativeSelect,
  SimpleGrid,
  Spinner,
  Table,
  Text,
} from '@chakra-ui/react';
import { Button, ExportMenu, StatCard, ToggleGroup } from '@themes/aegix';
import { SMSPageHeader as PageHeader } from '../components/layout/SMSPageHeader';
import { HiChartBar } from 'react-icons/hi2';
import dayjs from 'dayjs';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { useAlertTypes, useDeliveryMetrics, useSites } from '../hooks/queries';
import { toaster } from '../components/ui/toaster';
import { pollIntervalFor } from '../utils/pollIntervalFor';
import {
  buildDeliveryMetricsCsv,
  downloadBlob,
  downloadDeliveryMetricsPdf,
  exportFilename,
  formatSeconds,
  formatUsd,
} from '../utils/deliveryMetricsExport';
import type { DeliveryMetricsExportFilters } from '../utils/deliveryMetricsExport';
import type {
  DeliveryMetricsParams,
  TimelineEntry,
} from '../types/deliveryMetrics';

// Channel colors — kept consistent with the legend swatches below and the
// hand-rolled ChannelBar above.
const CHANNEL_COLORS: Record<string, string> = {
  FCM: '#3182CE',
  SMS: '#38A169',
  Email: '#D69E2E',
  Pending: '#A0AEC0',
};

// NOTE: TIME_WINDOW_OPTIONS and CATEGORY_OPTIONS are built inside the component
// so their labels are translated via t() when the language changes.

// Notification categories surfaced by the backend
// (NotificationMetrics.get_fcm_stats_by_category).
const CATEGORY_OPTION_VALUES = [
  { key: 'alert', value: 'alert' },
  { key: 'report', value: 'report' },
  { key: 'chat', value: 'chat' },
  { key: 'direct_message', value: 'direct_message' },
  { key: 'mention', value: 'mention' },
];

const TIME_WINDOW_VALUES = [
  { key: '5min', value: 5 },
  { key: '15min', value: 15 },
  { key: '1hour', value: 60 },
  { key: '6hours', value: 360 },
  { key: '24hours', value: 1440 },
];

function ChannelBar({
  fcm,
  sms,
  email,
  pending,
  total,
}: {
  fcm: number;
  sms: number;
  email: number;
  pending: number;
  total: number;
}) {
  if (total === 0) {
    return (
      <Box h="12px" bg="border.secondary" borderRadius="full" w="100%" />
    );
  }
  const pct = (n: number) => `${((n / total) * 100).toFixed(1)}%`;
  return (
    <Flex h="12px" borderRadius="full" overflow="hidden" w="100%">
      {fcm > 0 && (
        <Box bg="#3182CE" w={pct(fcm)} title={`FCM: ${fcm}`} />
      )}
      {sms > 0 && (
        <Box bg="#38A169" w={pct(sms)} title={`SMS: ${sms}`} />
      )}
      {email > 0 && (
        <Box bg="#D69E2E" w={pct(email)} title={`Email: ${email}`} />
      )}
      {pending > 0 && (
        <Box bg="border.secondary" w={pct(pending)} title={`Pending: ${pending}`} />
      )}
    </Flex>
  );
}

function TimelineBar({ entry, maxTotal }: { entry: TimelineEntry; maxTotal: number }) {
  return (
    <Flex alignItems="center" gap={3} py="4px">
      <Text fontSize="12px" color="text.secondary" flexShrink={0} w="60px" textAlign="right">
        {dayjs(entry.hour).format('HH:mm')}
      </Text>
      <Box flex="1" position="relative">
        <Flex h="16px" borderRadius="sm" overflow="hidden">
          {entry.sent > 0 && (
            <Box
              bg="#3182CE"
              w={`${maxTotal > 0 ? (entry.sent / maxTotal) * 100 : 0}%`}
            />
          )}
          {entry.failed > 0 && (
            <Box
              bg="#E53E3E"
              w={`${maxTotal > 0 ? (entry.failed / maxTotal) * 100 : 0}%`}
            />
          )}
          {entry.pending > 0 && (
            <Box
              bg="border.secondary"
              w={`${maxTotal > 0 ? (entry.pending / maxTotal) * 100 : 0}%`}
            />
          )}
        </Flex>
      </Box>
      <Text fontSize="12px" color="text.secondary" flexShrink={0} w="40px">
        {entry.total}
      </Text>
    </Flex>
  );
}

function formatRate(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

// --- Recharts cards ---------------------------------------------

function ChartCard({
  title,
  children,
  dataE2e,
}: {
  title: string;
  children: ReactNode;
  dataE2e?: string;
}) {
  return (
    <Box
      bg="surface.primary"
      border="1px solid"
      borderColor="border.secondary"
      borderRadius="lg"
      padding="16px"
      data-e2e={dataE2e}
    >
      <Text as="h2" fontSize="16px" fontWeight={600} color="text.primary" mb={3}>
        {title}
      </Text>
      {children}
    </Box>
  );
}

function ChartEmpty({ label }: { label: string }) {
  return (
    <Flex h="220px" align="center" justify="center">
      <Text fontSize="13px" color="text.secondary">
        {label}
      </Text>
    </Flex>
  );
}

// Chart 1 — delivery success rate over time (per-hour sent / total).
function SuccessRateLineChart({ timeline }: { timeline: TimelineEntry[] }) {
  const series = timeline.map((entry) => ({
    hour: dayjs(entry.hour).format('HH:mm'),
    rate: entry.total > 0 ? (entry.sent / entry.total) * 100 : 0,
  }));

  const { t } = useTranslation();
  if (series.length === 0) {
    return <ChartEmpty label={t('deliveryMetrics.noTimelineData', 'No timeline data available.')} />;
  }

  return (
    <ResponsiveContainer width="100%" height={220}>
      <LineChart data={series} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--chakra-colors-border-secondary)" />
        <XAxis
          dataKey="hour"
          tick={{ fontSize: 11, fill: 'var(--chakra-colors-text-secondary)' }}
          interval="preserveStartEnd"
        />
        <YAxis
          domain={[0, 100]}
          tickFormatter={(v: number) => `${v}%`}
          tick={{ fontSize: 11, fill: 'var(--chakra-colors-text-secondary)' }}
        />
        <Tooltip
          formatter={(value) => [`${Number(value).toFixed(1)}%`, t('deliveryMetrics.successRate', 'Success rate')]}
        />
        <Line
          type="monotone"
          dataKey="rate"
          name={t('deliveryMetrics.successRate', 'Success rate')}
          stroke="#3182CE"
          strokeWidth={2}
          dot={false}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

// Chart 2 — delivery channel breakdown (pie).
function ChannelPieChart({
  fcm,
  sms,
  email,
  pending,
}: {
  fcm: number;
  sms: number;
  email: number;
  pending: number;
}) {
  const data = [
    { name: 'FCM', value: fcm },
    { name: 'SMS', value: sms },
    { name: 'Email', value: email },
    { name: 'Pending', value: pending },
  ].filter((d) => d.value > 0);

  const { t } = useTranslation();
  if (data.length === 0) {
    return <ChartEmpty label={t('deliveryMetrics.noChannelData', 'No channel delivery data available.')} />;
  }

  return (
    <ResponsiveContainer width="100%" height={220}>
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          outerRadius={80}
          isAnimationActive={false}
          label={({ name, value }) => `${name}: ${value}`}
        >
          {data.map((entry) => (
            <Cell key={entry.name} fill={CHANNEL_COLORS[entry.name] ?? '#A0AEC0'} />
          ))}
        </Pie>
        <Tooltip />
      </PieChart>
    </ResponsiveContainer>
  );
}

// Chart 3 — fallback usage snapshot (bar). True time-series deferred to a
// backend ticket; this is a single-window snapshot of fallbacks vs. primary.
function FallbackBarChart({
  fallbackCount,
  fallbackRate,
  totalSent,
}: {
  fallbackCount: number;
  fallbackRate: number;
  totalSent: number;
}) {
  const { t } = useTranslation();
  const primary = Math.max(totalSent - fallbackCount, 0);
  const data = [
    { name: t('deliveryMetrics.primaryFCM', 'Primary (FCM)'), value: primary },
    { name: t('deliveryMetrics.fallbackSMSEmail', 'Fallback (SMS/Email)'), value: fallbackCount },
  ];

  if (totalSent === 0 && fallbackCount === 0) {
    return <ChartEmpty label={t('deliveryMetrics.noDeliveryData', 'No delivery data in this window.')} />;
  }

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--chakra-colors-border-secondary)" />
        <XAxis
          dataKey="name"
          tick={{ fontSize: 11, fill: 'var(--chakra-colors-text-secondary)' }}
        />
        <YAxis
          allowDecimals={false}
          tick={{ fontSize: 11, fill: 'var(--chakra-colors-text-secondary)' }}
        />
        <Tooltip
          formatter={(value, _name, item) => {
            const label = item?.payload?.name as string | undefined;
            if (label?.startsWith('Fallback')) {
              return [`${value} (${(fallbackRate * 100).toFixed(1)}%)`, 'Deliveries'];
            }
            return [`${value}`, 'Deliveries'];
          }}
        />
        <Bar dataKey="value" name="Deliveries" isAnimationActive={false} radius={[4, 4, 0, 0]}>
          <Cell fill="#3182CE" />
          <Cell fill="#D69E2E" />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

interface DeliveryMetricsProps {
  embedded?: boolean;
}

export function DeliveryMetrics({ embedded = false }: DeliveryMetricsProps) {
  const { t } = useTranslation();
  const [timeWindow, setTimeWindow] = useState(60);
  // Live polling. `live` is the user's preference (default on);
  // the *effective* polling state additionally requires no date range —
  // historical data doesn't change, so polling a fixed range is pointless.
  const [live, setLive] = useState(true);
  // Filters
  const [siteId, setSiteId] = useState<number | ''>('');
  const [category, setCategory] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // AlertType ("Protocol") filter for the AlertDelivery-backed
  // panels. Distinct taxonomy from `category` (NotificationLog strings).
  const [alertTypeId, setAlertTypeId] = useState<number | ''>('');

  // An explicit date range and the relative time-window pills are mutually
  // exclusive — the range overrides the window on the backend, so the pills
  // are greyed out while a range is active.
  const dateRangeActive = startDate !== '' || endDate !== '';

  const params: DeliveryMetricsParams = {
    timeline_hours: 24,
    alert_limit: 20,
  };
  if (dateRangeActive) {
    if (startDate) params.start = dayjs(startDate).startOf('day').toISOString();
    if (endDate) params.end = dayjs(endDate).endOf('day').toISOString();
  } else {
    params.time_window = timeWindow;
  }
  if (siteId !== '') params.site_id = siteId;
  if (category !== '') params.category = category;
  if (alertTypeId !== '') params.alert_type = alertTypeId;

  // Auto-pause while a date range is active; the user's Live preference is
  // kept in state so polling resumes as soon as the range is cleared.
  // Background-tab behavior: react-query's refetchIntervalInBackground stays
  // false, so a hidden tab does not keep hitting the API.
  const isPolling = live && !dateRangeActive;

  const { data, isLoading, isError, refetch, dataUpdatedAt } =
    useDeliveryMetrics(params, {
      pollMs: isPolling ? pollIntervalFor(timeWindow) : false,
    });

  const { data: sitesData } = useSites({ page_size: 100 });
  const sites = sitesData?.results ?? [];

  // Protocol (AlertType) options — scoped to the selected site when
  // one is chosen, since alert types are per-site.
  const { data: alertTypesData } = useAlertTypes(
    siteId === '' ? { page_size: 100 } : { school: siteId, page_size: 100 },
  );
  const alertTypes = alertTypesData?.results ?? [];

  // Build time window and category options inside the component so labels are reactive to language changes.
  const TIME_WINDOW_OPTIONS = TIME_WINDOW_VALUES.map((opt) => ({
    label: t(`deliveryMetrics.timeWindow.${opt.key}`, opt.key.replace('min', ' min').replace('hour', ' hour').replace('hours', ' hours')),
    value: opt.value,
  }));
  const CATEGORY_OPTIONS = CATEGORY_OPTION_VALUES.map((opt) => ({
    label: t(`deliveryMetrics.category.${opt.key}`, opt.key.replace('_', ' ')),
    value: opt.value,
  }));

  // CSV / PDF export of the current snapshot — reads the same `data`
  // object the page rendered (no refetch). The filter context is echoed into
  // the export header so a downloaded file is self-describing.
  const exportFilters: DeliveryMetricsExportFilters = {
    timeWindowMinutes: dateRangeActive ? undefined : timeWindow,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    siteName:
      siteId === ''
        ? undefined
        : sites.find((site) => site.id === siteId)?.name ?? `Site #${siteId}`,
    categoryLabel:
      category === ''
        ? undefined
        : CATEGORY_OPTIONS.find((opt) => opt.value === category)?.label ?? category,
    protocolLabel:
      alertTypeId === ''
        ? undefined
        : alertTypes.find((alertType) => alertType.id === alertTypeId)?.name ??
          `Protocol #${alertTypeId}`,
  };

  const notifyExportFailed = (format: 'CSV' | 'PDF', error: unknown) => {
    console.error(`Delivery metrics ${format} export failed`, error);
    toaster.create({
      title: t('deliveryMetrics.exportFailed', '{{format}} export failed', { format }),
      description: t('deliveryMetrics.exportFailedDesc', 'The file could not be generated. Check your connection and try again.'),
      type: 'error',
    });
  };

  const handleExportCsv = () => {
    if (!data) return;
    try {
      downloadBlob(
        buildDeliveryMetricsCsv(data, exportFilters),
        exportFilename('csv'),
        'text/csv;charset=utf-8',
      );
    } catch (error) {
      notifyExportFailed('CSV', error);
    }
  };

  const handleExportPdf = () => {
    if (!data) return;
    downloadDeliveryMetricsPdf(data, exportFilters).catch((error) => {
      notifyExportFailed('PDF', error);
    });
  };

  const timeWindowSelector = (
    <Flex
      gap={1}
      alignItems="center"
      opacity={dateRangeActive ? 0.4 : 1}
      title={dateRangeActive ? t('deliveryMetrics.timeWindowDisabled', 'Time window is disabled while a date range is active') : undefined}
    >
      {TIME_WINDOW_OPTIONS.map((opt) => (
        <Box
          key={opt.value}
          px="10px"
          py="4px"
          borderRadius="md"
          cursor={dateRangeActive ? 'not-allowed' : 'pointer'}
          pointerEvents={dateRangeActive ? 'none' : undefined}
          fontSize="13px"
          fontWeight={500}
          bg={timeWindow === opt.value ? 'surface.tertiary' : 'transparent'}
          color="text.primary"
          border="1px solid"
          borderColor={timeWindow === opt.value ? 'border.secondary' : 'transparent'}
          onClick={() => {
            if (!dateRangeActive) setTimeWindow(opt.value);
          }}
          data-e2e={`time-window-${opt.value}min`}
        >
          {opt.label}
        </Box>
      ))}
    </Flex>
  );

  // Live / Paused affordance + last-updated timestamp. While a date
  // range is active the toggle is forced to Paused and disabled (auto-pause).
  const liveControls = (
    <Flex gap={2} alignItems="center">
      <ToggleGroup
        options={[
          {
            value: 'live',
            label: t('deliveryMetrics.live', 'Live'),
            disabled: dateRangeActive,
            'data-e2e': 'metrics-live-toggle-live',
          },
          {
            value: 'paused',
            label: t('deliveryMetrics.paused', 'Paused'),
            disabled: dateRangeActive,
            'data-e2e': 'metrics-live-toggle-paused',
          },
        ]}
        value={isPolling ? 'live' : 'paused'}
        onChange={(value) => setLive(value === 'live')}
        aria-label={t('deliveryMetrics.liveUpdates', 'Live updates')}
        data-e2e="metrics-live-toggle"
      />
      {dataUpdatedAt > 0 && (
        <Text
          fontSize="12px"
          color="text.secondary"
          whiteSpace="nowrap"
          data-e2e="metrics-last-updated"
        >
          {t('deliveryMetrics.updated', 'Updated')} {dayjs(dataUpdatedAt).format('HH:mm:ss')}
        </Text>
      )}
    </Flex>
  );

  // Site / category / protocol / date-range filters next to
  // the time-window pills.
  const filtersBar = (
    <Flex gap={2} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
      {liveControls}
      <NativeSelect.Root size="sm" w="170px">
        <NativeSelect.Field
          aria-label={t('common.site', 'Site')}
          value={siteId === '' ? '' : String(siteId)}
          onChange={(e) => {
            setSiteId(e.target.value === '' ? '' : Number(e.target.value));
            // The protocol options re-scope with the site; the selected
            // protocol may not exist in the new scope.
            setAlertTypeId('');
          }}
          data-e2e="metrics-filter-site"
        >
          <option value="">{t('deliveryMetrics.allSites', 'All sites')}</option>
          {sites.map((site) => (
            <option key={site.id} value={site.id}>
              {site.name}
            </option>
          ))}
        </NativeSelect.Field>
      </NativeSelect.Root>
      <NativeSelect.Root size="sm" w="160px">
        <NativeSelect.Field
          aria-label={t('common.category', 'Category')}
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          data-e2e="metrics-filter-category"
        >
          <option value="">{t('deliveryMetrics.allCategories', 'All categories')}</option>
          {CATEGORY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </NativeSelect.Field>
      </NativeSelect.Root>
      <NativeSelect.Root size="sm" w="170px">
        <NativeSelect.Field
          aria-label={t('common.protocol', 'Protocol')}
          value={alertTypeId === '' ? '' : String(alertTypeId)}
          onChange={(e) =>
            setAlertTypeId(e.target.value === '' ? '' : Number(e.target.value))
          }
          data-e2e="metrics-filter-alert-type"
        >
          <option value="">{t('deliveryMetrics.allProtocols', 'All protocols')}</option>
          {alertTypes.map((alertType) => (
            <option key={alertType.id} value={alertType.id}>
              {alertType.name}
            </option>
          ))}
        </NativeSelect.Field>
      </NativeSelect.Root>
      <Input
        size="sm"
        type="date"
        w="150px"
        aria-label={t('common.startDate', 'Start date')}
        value={startDate}
        max={endDate || undefined}
        onChange={(e) => setStartDate(e.target.value)}
        data-e2e="metrics-filter-start"
      />
      <Input
        size="sm"
        type="date"
        w="150px"
        aria-label={t('common.endDate', 'End date')}
        value={endDate}
        min={startDate || undefined}
        onChange={(e) => setEndDate(e.target.value)}
        data-e2e="metrics-filter-end"
      />
      {timeWindowSelector}
      <ExportMenu
        e2eTitle="metrics-export"
        items={[
          {
            label: t('deliveryMetrics.exportCsv', 'Export CSV'),
            onClick: handleExportCsv,
            'data-e2e': 'metrics-export-csv',
          },
          {
            label: t('deliveryMetrics.exportPdf', 'Export PDF'),
            onClick: handleExportPdf,
            'data-e2e': 'metrics-export-pdf',
          },
        ]}
      />
    </Flex>
  );

  if (isLoading) {
    if (embedded) {
      return (
        <Flex flex="1" justify="center" align="center" py={20}>
          <Spinner size="lg" color="brand.500" />
        </Flex>
      );
    }
    return (
      <Box mx={-8} mt={-8}>
        <PageHeader
          icon={HiChartBar}
          title={t('deliveryMetrics.title', 'Delivery Metrics')}
          subtitle={t('deliveryMetrics.subtitle', 'Notification delivery overview')}
          showSearch={false}
          showNotifications={false}
        />
        <Flex flex="1" justify="center" align="center" py={20}>
          <Spinner size="lg" color="brand.500" />
        </Flex>
      </Box>
    );
  }

  if (isError || !data) {
    if (embedded) {
      return (
        <Flex flex="1" justify="center" align="center" direction="column" gap={3} py={20}>
          <Text color="text.secondary">{t('deliveryMetrics.loadFailed', 'Failed to load delivery metrics.')}</Text>
          <Button
            visual="secondary"
            size="sm"
            type="button"
            fontSize="14px"
            onClick={() => refetch()}
            data-e2e="delivery-metrics-retry-button"
          >
            {t('deliveryMetrics.retry', 'Retry')}
          </Button>
        </Flex>
      );
    }
    return (
      <Box mx={-8} mt={-8}>
        <PageHeader
          icon={HiChartBar}
          title={t('deliveryMetrics.title', 'Delivery Metrics')}
          subtitle={t('deliveryMetrics.subtitle', 'Notification delivery overview')}
          showSearch={false}
          showNotifications={false}
        />
        <Flex flex="1" justify="center" align="center" direction="column" gap={3} py={20}>
          <Text color="text.secondary">{t('deliveryMetrics.loadFailed', 'Failed to load delivery metrics.')}</Text>
          <Button
            visual="secondary"
            size="sm"
            type="button"
            fontSize="14px"
            onClick={() => refetch()}
            data-e2e="delivery-metrics-retry-button"
          >
            {t('deliveryMetrics.retry', 'Retry')}
          </Button>
        </Flex>
      </Box>
    );
  }

  const {
    fcm_delivery,
    channel_delivery,
    delivery_latency,
    cost,
    devices,
    stale_tokens,
    timeline,
    alert_summary,
    fcm_by_category,
    manager,
    providers,
    sms_delivery,
    email_delivery,
    failure_reasons,
  } = data;
  const maxTimelineTotal = Math.max(...(timeline.timeline.map((t) => t.total)), 1);
  const providerEntries = Object.entries(providers.providers ?? {});
  const failureReasons = failure_reasons.reasons ?? [];

  const content = (
    <Box padding="16px" overflow="auto">
        <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} gap="12px" mb={5}>
          <StatCard
            label={t('deliveryMetrics.stat.totalNotifications', 'Total Notifications')}
            value={fcm_delivery.total}
            subtext={
              dateRangeActive
                ? t('deliveryMetrics.selectedDateRange', 'Selected date range')
                : t('deliveryMetrics.lastNMin', 'Last {{n}} min', { n: fcm_delivery.time_window_minutes })
            }
            data-e2e="metrics-total-notifications-stat"
          />
          <StatCard
            label={t('deliveryMetrics.stat.fcmSuccessRate', 'FCM Success Rate')}
            value={formatRate(fcm_delivery.success_rate)}
            subtext={`${fcm_delivery.sent} ${t('deliveryMetrics.sent', 'sent')} / ${fcm_delivery.failed} ${t('deliveryMetrics.failed', 'failed')}`}
            data-e2e="metrics-fcm-success-rate-stat"
          />
          <StatCard
            label={t('deliveryMetrics.stat.totalDeliveries', 'Total Deliveries')}
            value={channel_delivery.total_deliveries}
            subtext={`${formatRate(channel_delivery.confirmation_rate)} ${t('deliveryMetrics.confirmed', 'confirmed')}`}
            data-e2e="metrics-total-deliveries-stat"
          />
          <StatCard
            label={t('deliveryMetrics.stat.activeDevices', 'Active Devices')}
            value={devices.total_devices}
            subtext={`iOS: ${devices.ios} / Android: ${devices.android} / Web: ${devices.web}`}
            data-e2e="metrics-active-devices-stat"
          />
          <StatCard
            label={t('deliveryMetrics.stat.fcmPending', 'FCM Pending')}
            value={fcm_delivery.pending}
            data-e2e="metrics-fcm-pending-stat"
          />
          <StatCard
            label={t('deliveryMetrics.stat.channelPending', 'Channel Pending')}
            value={channel_delivery.pending}
            data-e2e="metrics-channel-pending-stat"
          />
        </SimpleGrid>

        <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} gap="12px" mb={5}>
          <StatCard
            label={t('deliveryMetrics.stat.deliverySuccessRate', 'Delivery Success Rate')}
            value={formatRate(manager.overall_success_rate ?? 0)}
            subtext={`${manager.total_sent ?? 0} / ${manager.total_attempted ?? 0} ${t('deliveryMetrics.acrossChannels', 'across channels')}`}
            data-e2e="metrics-overall-success-rate-stat"
          />
          <StatCard
            label={t('deliveryMetrics.stat.smsSuccessRate', 'SMS Success Rate')}
            value={formatRate(sms_delivery.success_rate ?? 0)}
            subtext={`${sms_delivery.sent ?? 0} ${t('deliveryMetrics.sent', 'sent')} / ${sms_delivery.failed ?? 0} ${t('deliveryMetrics.failed', 'failed')}`}
            data-e2e="metrics-sms-success-rate-stat"
          />
          <StatCard
            label={t('deliveryMetrics.stat.emailSuccessRate', 'Email Success Rate')}
            value={formatRate(email_delivery.success_rate ?? 0)}
            subtext={`${email_delivery.sent ?? 0} ${t('deliveryMetrics.sent', 'sent')} / ${email_delivery.failed ?? 0} ${t('deliveryMetrics.failed', 'failed')}`}
            data-e2e="metrics-email-success-rate-stat"
          />
          <StatCard
            label={t('deliveryMetrics.stat.fallbacks', 'SMS/Email Fallbacks')}
            value={manager.fallback_count ?? 0}
            subtext={`${formatRate(manager.fallback_rate ?? 0)} ${t('deliveryMetrics.ofDeliveries', 'of deliveries')} · ${
              dateRangeActive
                ? t('deliveryMetrics.selectedRange', 'selected range')
                : t('deliveryMetrics.lastNMin', 'Last {{n}} min', { n: fcm_delivery.time_window_minutes })
            }`}
            data-e2e="metrics-fallbacks-stat"
          />
          {/* ADR-041 Phase 5: avg delivery time over
              client-confirmed deliveries (target ~3.2s; alert if >30s).
              avg/p95 arrive null when nothing was confirmed in the window —
              rendered as an em dash, never a fake 0.0s. */}
          <StatCard
            label={t('deliveryMetrics.stat.avgDeliveryTime', 'Avg Delivery Time')}
            value={formatSeconds(delivery_latency.avg_seconds)}
            subtext={`p95 ${formatSeconds(delivery_latency.p95_seconds)} · ${Math.max(
              delivery_latency.sample_count,
              0,
            )} ${t('deliveryMetrics.samples', 'samples')}`}
            data-e2e="metrics-avg-delivery-time-stat"
          />
          {/* ADR-041 Phase 5: stale-token metric (target <5%,
              alert >10%). Percentage arrives 0-100 from the backend; the
              error path omits everything but total_active (-1). */}
          <StatCard
            label={t('deliveryMetrics.stat.staleTokens', 'Stale Tokens')}
            value={stale_tokens.stale_count ?? 0}
            subtext={`${(stale_tokens.stale_percentage ?? 0).toFixed(1)}% of ${Math.max(
              stale_tokens.total_active,
              0,
            )} ${t('deliveryMetrics.active', 'active')} · >${stale_tokens.threshold_days ?? 30}d`}
            data-e2e="metrics-stale-tokens-stat"
          />
          {/* ADR-041 Phase 5: estimated cost per alert —
              settings-driven unit rates × sent counts, over distinct alerts
              with delivery activity (ADR cites ~$0.003/alert). Em dash on
              the backend error path (total_cost: -1), never a fake $0. */}
          <StatCard
            label={t('deliveryMetrics.stat.costPerAlert', 'Cost per Alert')}
            value={formatUsd(cost.cost_per_alert)}
            subtext={`${formatUsd(cost.total_cost)} ${t('deliveryMetrics.total', 'total')} · ${Math.max(
              cost.alert_count ?? 0,
              0,
            )} ${t('deliveryMetrics.alerts', 'alerts')}`}
            data-e2e="metrics-cost-per-alert-stat"
          />
        </SimpleGrid>

        {/* Charts */}
        <SimpleGrid columns={{ base: 1, lg: 3 }} gap="12px" mb={5}>
          <ChartCard title={t('deliveryMetrics.chart.successRateOverTime', 'Success Rate Over Time')} dataE2e="chart-success-rate-line">
            <SuccessRateLineChart timeline={timeline.timeline} />
          </ChartCard>
          <ChartCard title={t('deliveryMetrics.chart.deliveryChannels', 'Delivery Channels')} dataE2e="chart-channel-pie">
            <ChannelPieChart
              fcm={channel_delivery.fcm}
              sms={channel_delivery.sms}
              email={channel_delivery.email}
              pending={channel_delivery.pending}
            />
          </ChartCard>
          <ChartCard title={t('deliveryMetrics.chart.fallbackUsage', 'Fallback Usage')} dataE2e="chart-fallback-bar">
            <FallbackBarChart
              fallbackCount={manager.fallback_count ?? 0}
              fallbackRate={manager.fallback_rate ?? 0}
              totalSent={manager.total_sent ?? 0}
            />
          </ChartCard>
        </SimpleGrid>

        {/* Channel Breakdown */}
        <Box
          bg="surface.primary"
          border="1px solid"
          borderColor="border.secondary"
          borderRadius="lg"
          padding="16px"
          mb={5}
        >
          <Text as="h2" fontSize="16px" fontWeight={600} color="text.primary" mb={3}>
            {t('deliveryMetrics.channelBreakdown', 'Channel Breakdown')}
          </Text>
          <ChannelBar
            fcm={channel_delivery.fcm}
            sms={channel_delivery.sms}
            email={channel_delivery.email}
            pending={channel_delivery.pending}
            total={channel_delivery.total_deliveries}
          />
          <Flex mt={3} gap={5} flexWrap="wrap">
            <Flex alignItems="center" gap={2}>
              <Box w="12px" h="12px" borderRadius="sm" bg="#3182CE" />
              <Text fontSize="13px" color="text.secondary">
                FCM: {channel_delivery.fcm}
              </Text>
            </Flex>
            <Flex alignItems="center" gap={2}>
              <Box w="12px" h="12px" borderRadius="sm" bg="#38A169" />
              <Text fontSize="13px" color="text.secondary">
                SMS: {channel_delivery.sms}
              </Text>
            </Flex>
            <Flex alignItems="center" gap={2}>
              <Box w="12px" h="12px" borderRadius="sm" bg="#D69E2E" />
              <Text fontSize="13px" color="text.secondary">
                Email: {channel_delivery.email}
              </Text>
            </Flex>
            <Flex alignItems="center" gap={2}>
              <Box w="12px" h="12px" borderRadius="sm" bg="border.secondary" />
              <Text fontSize="13px" color="text.secondary">
                Pending: {channel_delivery.pending}
              </Text>
            </Flex>
          </Flex>
        </Box>

        <SimpleGrid columns={{ base: 1, lg: 2 }} gap="12px" mb={5}>
          {/* Category Breakdown */}
          <Box
            bg="surface.primary"
            border="1px solid"
            borderColor="border.secondary"
            borderRadius="lg"
            padding="16px"
          >
            <Text as="h2" fontSize="16px" fontWeight={600} color="text.primary" mb={3}>
              {t('deliveryMetrics.fcmByCategory', 'FCM by Category')}
            </Text>
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader color="text.secondary" fontSize="12px" fontWeight={600}>
                    {t('deliveryMetrics.col.category', 'Category')}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color="text.secondary" fontSize="12px" fontWeight={600} textAlign="right">
                    {t('deliveryMetrics.col.sent', 'Sent')}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color="text.secondary" fontSize="12px" fontWeight={600} textAlign="right">
                    {t('deliveryMetrics.col.failed', 'Failed')}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color="text.secondary" fontSize="12px" fontWeight={600} textAlign="right">
                    {t('deliveryMetrics.col.total', 'Total')}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color="text.secondary" fontSize="12px" fontWeight={600} textAlign="right">
                    {t('deliveryMetrics.col.rate', 'Rate')}
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {Object.entries(fcm_by_category)
                  .filter(([key]) => key !== 'overall')
                  .map(([category, stats]) => (
                    <Table.Row key={category}>
                      <Table.Cell color="text.primary" fontSize="13px" textTransform="capitalize">
                        {category.replace('_', ' ')}
                      </Table.Cell>
                      <Table.Cell color="text.primary" fontSize="13px" textAlign="right">
                        {stats.sent}
                      </Table.Cell>
                      <Table.Cell color={stats.failed > 0 ? '#E53E3E' : 'text.primary'} fontSize="13px" textAlign="right">
                        {stats.failed}
                      </Table.Cell>
                      <Table.Cell color="text.primary" fontSize="13px" textAlign="right">
                        {stats.total}
                      </Table.Cell>
                      <Table.Cell color="text.primary" fontSize="13px" textAlign="right">
                        {formatRate(stats.success_rate)}
                      </Table.Cell>
                    </Table.Row>
                  ))}
              </Table.Body>
            </Table.Root>
          </Box>

          {/* Device Distribution */}
          <Box
            bg="surface.primary"
            border="1px solid"
            borderColor="border.secondary"
            borderRadius="lg"
            padding="16px"
          >
            <Text as="h2" fontSize="16px" fontWeight={600} color="text.primary" mb={3}>
              {t('deliveryMetrics.deviceDistribution', 'Device Distribution')}
            </Text>
            <SimpleGrid columns={3} gap="12px">
              <Box textAlign="center">
                <Text fontSize="28px" fontWeight={700} color="text.primary">
                  {devices.ios}
                </Text>
                <Text fontSize="12px" color="text.secondary">iOS</Text>
              </Box>
              <Box textAlign="center">
                <Text fontSize="28px" fontWeight={700} color="text.primary">
                  {devices.android}
                </Text>
                <Text fontSize="12px" color="text.secondary">Android</Text>
              </Box>
              <Box textAlign="center">
                <Text fontSize="28px" fontWeight={700} color="text.primary">
                  {devices.web}
                </Text>
                <Text fontSize="12px" color="text.secondary">Web</Text>
              </Box>
            </SimpleGrid>
            <Box mt={3}>
              <Flex h="12px" borderRadius="full" overflow="hidden" w="100%">
                {devices.ios > 0 && (
                  <Box
                    bg="#805AD5"
                    w={`${(devices.ios / Math.max(devices.total_devices, 1)) * 100}%`}
                  />
                )}
                {devices.android > 0 && (
                  <Box
                    bg="#38A169"
                    w={`${(devices.android / Math.max(devices.total_devices, 1)) * 100}%`}
                  />
                )}
                {devices.web > 0 && (
                  <Box
                    bg="#3182CE"
                    w={`${(devices.web / Math.max(devices.total_devices, 1)) * 100}%`}
                  />
                )}
              </Flex>
              <Flex mt={2} gap={4} flexWrap="wrap">
                <Flex alignItems="center" gap={1}>
                  <Box w="10px" h="10px" borderRadius="sm" bg="#805AD5" />
                  <Text fontSize="12px" color="text.secondary">iOS</Text>
                </Flex>
                <Flex alignItems="center" gap={1}>
                  <Box w="10px" h="10px" borderRadius="sm" bg="#38A169" />
                  <Text fontSize="12px" color="text.secondary">Android</Text>
                </Flex>
                <Flex alignItems="center" gap={1}>
                  <Box w="10px" h="10px" borderRadius="sm" bg="#3182CE" />
                  <Text fontSize="12px" color="text.secondary">Web</Text>
                </Flex>
              </Flex>
            </Box>
          </Box>
        </SimpleGrid>

        <SimpleGrid columns={{ base: 1, lg: 2 }} gap="12px" mb={5}>
          {/* By Provider */}
          <Box
            bg="surface.primary"
            border="1px solid"
            borderColor="border.secondary"
            borderRadius="lg"
            padding="16px"
          >
            <Text as="h2" fontSize="16px" fontWeight={600} color="text.primary" mb={3}>
              {t('deliveryMetrics.byProvider', 'By Provider')}
            </Text>
            {providerEntries.length === 0 ? (
              <Text fontSize="13px" color="text.secondary" textAlign="center" py={4}>
                {t('deliveryMetrics.noProviderData', 'No provider data available.')}
              </Text>
            ) : (
              <Table.Root size="sm">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader color="text.secondary" fontSize="12px" fontWeight={600}>
                      {t('deliveryMetrics.col.provider', 'Provider')}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color="text.secondary" fontSize="12px" fontWeight={600} textAlign="right">
                      {t('deliveryMetrics.col.total', 'Total')}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color="text.secondary" fontSize="12px" fontWeight={600} textAlign="right">
                      {t('deliveryMetrics.col.sent', 'Sent')}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color="text.secondary" fontSize="12px" fontWeight={600} textAlign="right">
                      {t('deliveryMetrics.col.failed', 'Failed')}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color="text.secondary" fontSize="12px" fontWeight={600} textAlign="right">
                      {t('deliveryMetrics.col.rate', 'Rate')}
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {providerEntries.map(([name, stat]) => (
                    <Table.Row key={name}>
                      <Table.Cell color="text.primary" fontSize="13px" textTransform="capitalize">
                        {name.replace(/_/g, ' ')}
                      </Table.Cell>
                      <Table.Cell color="text.primary" fontSize="13px" textAlign="right">
                        {stat.total}
                      </Table.Cell>
                      <Table.Cell color="text.primary" fontSize="13px" textAlign="right">
                        {stat.sent}
                      </Table.Cell>
                      <Table.Cell color={stat.failed > 0 ? '#E53E3E' : 'text.primary'} fontSize="13px" textAlign="right">
                        {stat.failed}
                      </Table.Cell>
                      <Table.Cell color="text.primary" fontSize="13px" textAlign="right">
                        {formatRate(stat.success_rate)}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            )}
          </Box>

          {/* Recent Failure Reasons */}
          <Box
            bg="surface.primary"
            border="1px solid"
            borderColor="border.secondary"
            borderRadius="lg"
            padding="16px"
          >
            <Text as="h2" fontSize="16px" fontWeight={600} color="text.primary" mb={3}>
              {t('deliveryMetrics.recentFailureReasons', 'Recent Failure Reasons')}
            </Text>
            {failureReasons.length === 0 ? (
              <Text fontSize="13px" color="text.secondary" textAlign="center" py={4}>
                {t('deliveryMetrics.noFailures', 'No failures in this window.')}
              </Text>
            ) : (
              <Table.Root size="sm">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader color="text.secondary" fontSize="12px" fontWeight={600}>
                      {t('deliveryMetrics.col.channel', 'Channel')}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color="text.secondary" fontSize="12px" fontWeight={600}>
                      {t('deliveryMetrics.col.reason', 'Reason')}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color="text.secondary" fontSize="12px" fontWeight={600} textAlign="right">
                      {t('deliveryMetrics.col.count', 'Count')}
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {failureReasons.map((reason, idx) => (
                    <Table.Row key={`${reason.channel}-${reason.failure_reason}-${idx}`}>
                      <Table.Cell color="text.primary" fontSize="13px" textTransform="uppercase">
                        {reason.channel}
                      </Table.Cell>
                      <Table.Cell color="text.primary" fontSize="13px">
                        {reason.failure_reason}
                      </Table.Cell>
                      <Table.Cell color="#E53E3E" fontSize="13px" textAlign="right">
                        {reason.count}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            )}
          </Box>
        </SimpleGrid>

        {/* Delivery Timeline */}
        <Box
          bg="surface.primary"
          border="1px solid"
          borderColor="border.secondary"
          borderRadius="lg"
          padding="16px"
          mb={5}
        >
          <Flex justifyContent="space-between" alignItems="center" mb={3}>
            <Text as="h2" fontSize="16px" fontWeight={600} color="text.primary">
              {t('deliveryMetrics.deliveryTimeline', 'Delivery Timeline (24h)')}
            </Text>
            <Flex gap={4}>
              <Flex alignItems="center" gap={1}>
                <Box w="10px" h="10px" borderRadius="sm" bg="#3182CE" />
                <Text fontSize="12px" color="text.secondary">{t('deliveryMetrics.col.sent', 'Sent')}</Text>
              </Flex>
              <Flex alignItems="center" gap={1}>
                <Box w="10px" h="10px" borderRadius="sm" bg="#E53E3E" />
                <Text fontSize="12px" color="text.secondary">{t('deliveryMetrics.col.failed', 'Failed')}</Text>
              </Flex>
              <Flex alignItems="center" gap={1}>
                <Box w="10px" h="10px" borderRadius="sm" bg="border.secondary" />
                <Text fontSize="12px" color="text.secondary">{t('deliveryMetrics.pending', 'Pending')}</Text>
              </Flex>
            </Flex>
          </Flex>
          {timeline.timeline.length === 0 ? (
            <Text fontSize="13px" color="text.secondary" textAlign="center" py={4}>
              {t('deliveryMetrics.noTimelineData', 'No timeline data available.')}
            </Text>
          ) : (
            <Box>
              {timeline.timeline.map((entry) => (
                <TimelineBar key={entry.hour} entry={entry} maxTotal={maxTimelineTotal} />
              ))}
            </Box>
          )}
        </Box>

        {/* Per-Alert Delivery Table */}
        <Box
          bg="surface.primary"
          border="1px solid"
          borderColor="border.secondary"
          borderRadius="lg"
          padding="16px"
        >
          <Text as="h2" fontSize="16px" fontWeight={600} color="text.primary" mb={3}>
            {t('deliveryMetrics.recentAlertDeliveries', 'Recent Alert Deliveries')}
          </Text>
          {alert_summary.alerts.length === 0 ? (
            <Text fontSize="13px" color="text.secondary" textAlign="center" py={4}>
              {t('deliveryMetrics.noAlertData', 'No alert delivery data available.')}
            </Text>
          ) : (
            <Box overflowX="auto">
              <Table.Root size="sm">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader color="text.secondary" fontSize="12px" fontWeight={600}>
                      {t('deliveryMetrics.col.alertId', 'Alert ID')}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color="text.secondary" fontSize="12px" fontWeight={600} textAlign="right">
                      {t('deliveryMetrics.col.total', 'Total')}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color="text.secondary" fontSize="12px" fontWeight={600} textAlign="right">
                      FCM
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color="text.secondary" fontSize="12px" fontWeight={600} textAlign="right">
                      SMS
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color="text.secondary" fontSize="12px" fontWeight={600} textAlign="right">
                      Email
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color="text.secondary" fontSize="12px" fontWeight={600} textAlign="right">
                      {t('deliveryMetrics.pending', 'Pending')}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color="text.secondary" fontSize="12px" fontWeight={600} textAlign="right">
                      {t('deliveryMetrics.col.channels', 'Channels')}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color="text.secondary" fontSize="12px" fontWeight={600} textAlign="right">
                      {t('deliveryMetrics.col.lastActivity', 'Last Activity')}
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {alert_summary.alerts.map((alert) => (
                    <Table.Row key={alert.alert_id}>
                      <Table.Cell color="text.primary" fontSize="13px" fontWeight={500}>
                        #{alert.alert_id}
                      </Table.Cell>
                      <Table.Cell color="text.primary" fontSize="13px" textAlign="right">
                        {alert.total}
                      </Table.Cell>
                      <Table.Cell color="text.primary" fontSize="13px" textAlign="right">
                        {alert.fcm}
                      </Table.Cell>
                      <Table.Cell color="text.primary" fontSize="13px" textAlign="right">
                        {alert.sms}
                      </Table.Cell>
                      <Table.Cell color="text.primary" fontSize="13px" textAlign="right">
                        {alert.email}
                      </Table.Cell>
                      <Table.Cell
                        color={alert.pending > 0 ? '#E53E3E' : 'text.primary'}
                        fontSize="13px"
                        textAlign="right"
                      >
                        {alert.pending}
                      </Table.Cell>
                      <Table.Cell textAlign="right">
                        <ChannelBar
                          fcm={alert.fcm}
                          sms={alert.sms}
                          email={alert.email}
                          pending={alert.pending}
                          total={alert.total}
                        />
                      </Table.Cell>
                      <Table.Cell color="text.secondary" fontSize="12px" textAlign="right">
                        {alert.latest ? dayjs(alert.latest).format('MMM DD, HH:mm') : '-'}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          )}
        </Box>
      </Box>
  );

  if (embedded) {
    return (
      <Box>
        <Flex justifyContent="flex-end" mb={3} px={4} pt={4}>
          {filtersBar}
        </Flex>
        {content}
      </Box>
    );
  }

  return (
    <Box mx={-8} mt={-8}>
      <PageHeader
        icon={HiChartBar}
        title={t('deliveryMetrics.title', 'Delivery Metrics')}
        subtitle={t('deliveryMetrics.subtitle', 'Notification delivery overview')}
        showSearch={false}
        showNotifications={false}
      />
      <Flex justifyContent="flex-end" mb={3} px={4} pt={4}>
        {filtersBar}
      </Flex>
      {content}
    </Box>
  );
}
